// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';

import { useHoleFeatures } from '@/features/course-trace/useHoleFeatures';
import { resolveRoundCourseId } from '@/services/courseTraceUpload';

import { shapesNearHole, type HoleShape } from './holeShapes';
import type { LatLon } from './tileMath';

/** A tee box, carrying its course name ("Yellow") when one is known. */
export type TeePoint = LatLon & { label: string | null };

export type HoleViewGeometry = {
  /** Every derived tee box on this hole, lowest ordinal first. */
  tees: TeePoint[];
  /** Derived green centre, or null when the course has not been mapped that far. */
  green: LatLon | null;
  /**
   * The mapped boundaries reaching into this hole's frame, in draw order.
   *
   * Empty on a course the satellite analysis has not run over, which is the ordinary case
   * rather than an error — the view falls back to plain imagery.
   */
  shapes: HoleShape[];
  loading: boolean;
  /** True once the lookups finished and the hole has no green to frame. */
  unavailable: boolean;
  /** The round's course, once resolved. Lets the modal link to course-level admin views. */
  courseId: string | null;
};

export type UseHoleViewGeometryParams = {
  apiBaseUrl: string;
  token: string | null | undefined;
  roundId: string | null;
  holeNumber: number;
  /** Nothing is fetched until the view is actually opened. */
  enabled: boolean;
};

/** Course lookup result, tagged with the round it belongs to so a round change resets it. */
type CourseLookup = { roundId: string; courseId: string | null };

/**
 * Tee and green positions for one hole of the round in play.
 *
 * The hole screen carries a round id, not a course id, so the course is resolved the same
 * way course-mapping capture resolves it. Both lookups are deferred until the view opens:
 * this is a button most players press rarely, and it should cost nothing on the holes
 * where they do not.
 *
 * Positions come from course mapping — imported geometry refined by players' GPS traces —
 * so a hole can legitimately have none, as can a whole course. That is reported as
 * `unavailable` rather than as an error, because there is nothing for the player to retry.
 */
export const useHoleViewGeometry = ({
  apiBaseUrl,
  token,
  roundId,
  holeNumber,
  enabled,
}: UseHoleViewGeometryParams): HoleViewGeometry => {
  const [lookup, setLookup] = useState<CourseLookup | null>(null);
  const resolvedCourseId = lookup && lookup.roundId === roundId ? lookup.courseId : null;
  const courseResolved = lookup !== null && lookup.roundId === roundId;

  useEffect(() => {
    if (!enabled || !roundId || !token || courseResolved) return undefined;

    let cancelled = false;
    void resolveRoundCourseId(apiBaseUrl, roundId, token).then((courseId) => {
      if (!cancelled) setLookup({ roundId, courseId });
    });

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseResolved, enabled, roundId, token]);

  const { features, shapes, loading } = useHoleFeatures(
    apiBaseUrl,
    token,
    enabled ? resolvedCourseId : null
  );

  const geometry = useMemo(() => {
    const forHole = features.filter((feature) => feature.holeNumber === holeNumber);
    const tees = forHole
      .filter((feature) => feature.kind === 'tee')
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((feature) => ({ lat: feature.lat, lon: feature.lon, label: feature.name }));
    // Greens carry ordinal 0; if a course ever derives more than one, the most trusted wins.
    const greenFeature = forHole
      .filter((feature) => feature.kind === 'green')
      .sort((a, b) => b.confidence - a.confidence)[0];
    const green = greenFeature ? { lat: greenFeature.lat, lon: greenFeature.lon } : null;
    // Picked by where the boundaries lie rather than by the hole number on the row: the
    // cover classes are cut from the whole course at once and attributed by centroid, so
    // half of what belongs in this hole's picture is filed under its neighbour.
    return { tees, green, shapes: shapesNearHole(shapes, tees, green) };
  }, [features, holeNumber, shapes]);

  // Signing out or losing the round id leaves nothing to wait for, so those are not "loading".
  const waitingForCourse = enabled && !!roundId && !!token && !courseResolved;
  const busy = waitingForCourse || (enabled && loading);

  return {
    ...geometry,
    loading: busy,
    unavailable: enabled && !busy && geometry.green === null,
    courseId: resolvedCourseId,
  };
};
